import { isSameDay } from 'date-fns'
import { isUndefined } from 'lodash-es'
import { useCallback } from 'react'

import { TLastActiveContract } from '@/entities/contract'
import { roomQueries } from '@/entities/room'
import { usePrefetchQuery } from '@/shared/api'
import {
  DateRange,
  formatDate,
  OpenOptions,
  today,
  useDialog,
} from '@/shared/lib'

export const DIALOG = {
  CHECK_EXTEND_CONTRACT: 'CHECK_EXTEND_CONTRACT',
  IN_PROGRESS_CONTRACT: 'IN_PROGRESS_CONTRACT',
  END_DATE_CALENDAR: 'END_DATE_CALENDAR',
  PERIOD_CALENDAR: 'PERIOD_CALENDAR',
  BLOCKED_USER: 'BLOCKED_USER',
} as const

export type ContractDialog = keyof typeof DIALOG

type UseContractDialogProps = {
  roomId: number
  updatePeriod: (period: DateRange) => void
  lastActiveContract?: TLastActiveContract | null
}

export const useContractDialog = ({
  roomId,
  updatePeriod,
  lastActiveContract,
}: UseContractDialogProps) => {
  const { prefetch } = usePrefetchQuery()

  const { isOpen, open, close, closeHandler } = useDialog<ContractDialog>()

  const getIsTodayBlocked = useCallback(async () => {
    const { success, data } = await prefetch(
      roomQueries.roomSchedules({
        roomId,
        year: today.getFullYear(),
        month: today.getMonth() + 1,
      }),
    )

    if (!success) {
      return
    }

    const isTodayBlocked = data.schedules.some(schedule =>
      isSameDay(schedule.date, today),
    )

    return isTodayBlocked
  }, [roomId])

  const getRoomAvailableWeeks = useCallback(
    async ({ isExtend = false }: { isExtend: boolean }) => {
      const { success } = await prefetch(
        roomQueries.roomAvailableWeeks({
          roomId,
          startDate: formatDate({
            date: lastActiveContract?.extendContractStartDate,
            format: 'yyyy-MM-dd',
          }),
          isExtend,
        }),
      )

      return { success }
    },
    [roomId, lastActiveContract],
  )

  const handlePeriodDialogOpen = useCallback(async () => {
    const isTodayBlocked = await getIsTodayBlocked()

    const success = !isUndefined(isTodayBlocked)

    if (isTodayBlocked) {
      updatePeriod({
        from: undefined,
        to: undefined,
      })
    }

    return { success }
  }, [getIsTodayBlocked])

  const handleEndDateDialogOpen = useCallback(async () => {
    const { success } = await getRoomAvailableWeeks({ isExtend: true })

    return { success }
  }, [getRoomAvailableWeeks])

  const openDialog = useCallback(
    async (key: ContractDialog, options?: OpenOptions) => {
      if (key === DIALOG.PERIOD_CALENDAR) {
        const { success } = await handlePeriodDialogOpen()

        if (!success) {
          return
        }
      }

      if (key === DIALOG.END_DATE_CALENDAR) {
        const { success } = await handleEndDateDialogOpen()

        if (!success) {
          return
        }
      }

      open(key, options)
    },
    [handlePeriodDialogOpen, open],
  )

  return {
    isOpen,
    openDialog,
    close,
    closeHandler,
  }
}
